import React, { useState } from 'react';
import { CompanyEmployee, EmployeeCreateRequest, EmployeeUpdateRequest, EmployeeInviteRequest, UserRole } from '../../types/auth';
import { EmployeeForm } from './EmployeeForm';
import { EmployeeInviteForm } from './EmployeeInviteForm';
import { useAuth } from '../../contexts/AuthContext';

interface EmployeeListProps {
    employees: CompanyEmployee[];
    onAdd: (data: EmployeeCreateRequest) => void;
    onUpdate: (employeeId: number, data: EmployeeUpdateRequest) => void;
    onRemove: (employeeId: number) => void;
    onInvite?: (data: EmployeeInviteRequest) => void;
    companyId?: number;
}

export const EmployeeList: React.FC<EmployeeListProps> = ({ employees, onAdd, onUpdate, onRemove, onInvite, companyId }) => {
    const { user } = useAuth();
    const [showAddForm, setShowAddForm] = useState(false);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState<CompanyEmployee | null>(null);
    const [isDeleting, setIsDeleting] = useState<number | null>(null);

    const handleAddEmployee = async (data: any) => {
        await onAdd(data);
        setShowAddForm(false);
    };

    const handleUpdateEmployee = async (data: any) => {
        if (editingEmployee?.id) {
            await onUpdate(editingEmployee.id, data);
            setEditingEmployee(null);
        }
    };

    const handleRemoveEmployee = async (employeeId: number) => {
        if (window.confirm('Вы уверены, что хотите удалить этого сотрудника?')) {
            setIsDeleting(employeeId);
            try {
                await onRemove(employeeId);
            } finally {
                setIsDeleting(null);
            }
        }
    };

    const handleInviteEmployee = async (data: EmployeeInviteRequest) => {
        if (onInvite) {
            await onInvite(data);
            setShowInviteForm(false);
        }
    };

    const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

    const getFullName = (employee: CompanyEmployee) => {
        const parts = [employee.lastName, employee.firstName, employee.middleName].filter(Boolean);
        return parts.join(' ');
    };

    return (
        <div className="max-w-6xl">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Сотрудники компании</h2>
                    <p className="text-gray-600">Управляйте списком сотрудников вашей компании</p>
                </div>
                <div className="flex space-x-3">
                    {isSuperAdmin && onInvite && (
                        <button
                            onClick={() => setShowInviteForm(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center space-x-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            <span>Пригласить сотрудника</span>
                        </button>
                    )}
                    {isSuperAdmin && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center space-x-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Добавить сотрудника</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Add Employee Form Modal */}
            {showAddForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Добавить сотрудника</h3>
                            <button
                                onClick={() => setShowAddForm(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <EmployeeForm
                            onSubmit={handleAddEmployee}
                            onCancel={() => setShowAddForm(false)}
                            isSubmitting={false}
                        />
                    </div>
                </div>
            )}

            {/* Invite Employee Form Modal */}
            {showInviteForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Пригласить сотрудника</h3>
                            <button
                                onClick={() => setShowInviteForm(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <EmployeeInviteForm
                            onSubmit={handleInviteEmployee}
                            onCancel={() => setShowInviteForm(false)}
                            isSubmitting={false}
                        />
                    </div>
                </div>
            )}

            {/* Edit Employee Form Modal */}
            {editingEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Редактировать сотрудника</h3>
                            <button
                                onClick={() => setEditingEmployee(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <EmployeeForm
                            employee={editingEmployee}
                            onSubmit={handleUpdateEmployee}
                            onCancel={() => setEditingEmployee(null)}
                            isSubmitting={false}
                        />
                    </div>
                </div>
            )}

            {/* Employees List */}
            {employees.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Нет сотрудников</h3>
                    <p className="mt-1 text-sm text-gray-500">Начните с добавления первого сотрудника.</p>
                </div>
            ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                        {employees.map((employee) => (
                            <li key={employee.id} className="px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-medium text-sm">
                                                    {getFullName(employee).charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {getFullName(employee)}
                                                </p>
                                            </div>
                                            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                                                <span>{employee.position}</span>
                                                {employee.phoneNumber && (
                                                    <span>{employee.phoneNumber}</span>
                                                )}
                                                {employee.email && (
                                                    <span>{employee.email}</span>
                                                )}
                                            </div>
                                            {employee.hireDate && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Принят: {new Date(employee.hireDate).toLocaleDateString('ru-RU')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {isSuperAdmin && (
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => setEditingEmployee(employee)}
                                                className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                            >
                                                Редактировать
                                            </button>
                                            <button
                                                onClick={() => handleRemoveEmployee(employee.id!)}
                                                disabled={isDeleting === employee.id}
                                                className="text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50"
                                            >
                                                {isDeleting === employee.id ? 'Удаление...' : 'Удалить'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
